import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, AlertCircle, Clock, Anchor } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

/**
 * Landing page hit after the backend processes a "Confirm job" magic link.
 *
 * The backend redirects here with one of these `status` query params:
 *   - confirmed : token consumed; the job was just flipped to `confirmed`.
 *   - already   : token was already used (or job was already confirmed).
 *   - invalid   : unknown token.
 *   - expired   : token TTL exceeded (7 days).
 *   - error     : unexpected failure.
 *
 * No backend call happens here — by the time the user lands on this page
 * the confirmation has already happened (or failed) server-side. This
 * keeps the magic link single-trip and idempotent on double-clicks.
 */
export default function ConfirmJobLanding() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const status = params.get("status") || "error";
  const jobId = params.get("job_id");
  const yachtId = params.get("yacht_id");

  const view = useMemo(() => {
    switch (status) {
      case "confirmed":
        return {
          icon: CheckCircle2,
          tone: "emerald",
          title: "Job confirmed",
          body: "Thanks for confirming. The service provider has been notified and can now upload the final invoice — it will land in the yacht's Invoices section automatically.",
        };
      case "already":
        return {
          icon: CheckCircle2,
          tone: "emerald",
          title: "Already confirmed",
          body: "This job has already been confirmed. Nothing more to do — the provider has been notified.",
        };
      case "expired":
        return {
          icon: Clock,
          tone: "amber",
          title: "Link expired",
          body: "This confirmation link is older than 7 days, so it's no longer valid. Sign in to Seavanto and change the job's status from the Tasks page.",
        };
      case "invalid":
        return {
          icon: AlertCircle,
          tone: "red",
          title: "Link not valid",
          body: "This link was already used or has been replaced by a newer one. Sign in to Seavanto to review the job.",
        };
      default:
        return {
          icon: AlertCircle,
          tone: "red",
          title: "Something went wrong",
          body: "We couldn't process the confirmation. Sign in to Seavanto and change the job's status manually.",
        };
    }
  }, [status]);

  // For nice page titles in the browser tab.
  useEffect(() => {
    document.title = `${view.title} · Seavanto`;
  }, [view.title]);

  const Icon = view.icon;
  const colorClasses = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    red: "bg-red-50 text-red-700 border-red-200",
  }[view.tone];

  // Authenticated users get a direct deep-link to the job; signed-out
  // recipients (likely owners who clicked the email on a different device
  // than the one they normally log in on) get a sign-in CTA that lands
  // them on the same job afterward.
  const signedIn = !!user;
  const jobLink = jobId
    ? `/app/tasks${yachtId ? `?yacht_id=${yachtId}` : ""}`
    : "/app";

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-zinc-50">
      <div className="max-w-md w-full bg-white border border-zinc-200 rounded-xl p-8 shadow-sm" data-testid="confirm-landing">
        <Anchor className="h-7 w-7 text-ink mb-4" />
        <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${colorClasses}`}>
          <Icon className="h-3.5 w-3.5" />
          {view.title}
        </div>
        <h1 className="font-display text-2xl font-medium tracking-tight mt-4">{view.title}</h1>
        <p className="text-sm text-zinc-600 mt-2 leading-relaxed" data-testid="confirm-body">{view.body}</p>

        <div className="mt-6 flex flex-wrap gap-2">
          {signedIn ? (
            <Link to={jobLink}>
              <Button className="bg-ink hover:bg-black" data-testid="confirm-go-to-job">
                Go to job
              </Button>
            </Link>
          ) : (
            <Link to={`/login?next=${encodeURIComponent(jobLink)}`}>
              <Button className="bg-ink hover:bg-black" data-testid="confirm-sign-in">
                Sign in to Seavanto
              </Button>
            </Link>
          )}
          <a href="https://seavanto.io" className="inline-flex items-center px-4 py-2 text-sm text-zinc-600 hover:text-ink">
            {t("auth.back_to_signin", { defaultValue: "Back to Seavanto" })}
          </a>
        </div>
      </div>
    </div>
  );
}
